import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Router from "next/router";

import TextInput from "@kikoff/components/src/v1/inputs/TextInput";
import useArray from "@kikoff/hooks/src/useArray";
import useErrorConsumer from "@kikoff/hooks/src/useErrorConsumer";
import { promiseDelay } from "@kikoff/utils/src/general";

import {
  StatusButton,
  StyledButton,
  UnderlineButton,
} from "@component/buttons/buttons";
import PageAlign from "@component/dashboard/offer/page_align";
import { LabeledCheckbox } from "@component/inputs/checkbox";
import Line from "@component/line";
import { closeLoan, selectCbsLoan } from "@feature/loans";
import { track } from "@util/analytics";
import { nativeDispatch } from "@util/mobile";

import styles from "./close-loan.module.scss";

export default function CloseLoanPage() {
  const [viewIdx, setViewIdx] = useState(0);

  const loan = useSelector(selectCbsLoan({ includeClosed: false }));

  if (!loan?.allowClosure) {
    Router.replace("/dashboard");
    return null;
  }

  const views = [
    <FinalUpsellView onContinue={() => setViewIdx(viewIdx + 1)} />,
    <FinalizeClosureView
      onContinue={() => setViewIdx(viewIdx + 1)}
      loan={loan}
    />,
    <CloseConfirmedView onContinue={goToDashboard} />,
  ];

  return (
    <PageAlign>
      <div className={styles["close-loan-page"]}>
        <div className={styles.title}>
          <span
            onClick={() =>
              viewIdx === 1 ? setViewIdx(viewIdx - 1) : goToDashboard()
            }
          >
            
          </span>
          <span> Close my loan early </span>
          <span> </span>
        </div>
        {views[viewIdx]}
      </div>
    </PageAlign>
  );
}

function FinalUpsellView({ onContinue }) {
  return (
    <div className={styles.body}>
      <div className={styles["are-you-sure"]}>Are you sure?</div>

      <div className={styles["upsell-text"]}>
        <p>
          The Credit Builder Loan can help you build both positive payment
          history and savings.
        </p>
        <p>
          Keeping it open can also help you increase the average age of your
          accounts over time.
        </p>
      </div>

      <div className={styles["action-buttons"]}>
        <UnderlineButton
          className={styles["close-my-loan"]}
          onClick={() => {
            onContinue();
            track("CBL - Close - Selected Closure Reason");
          }}
        >
          Close my loan anyway
        </UnderlineButton>

        <StyledButton
          className={styles["keep-building-credit"]}
          shape="round"
          onClick={goToDashboard}
        >
          Keep building credit
        </StyledButton>
      </div>
    </div>
  );
}

function FinalizeClosureView({ onContinue, loan }) {
  const dispatch = useDispatch();
  const [checked, setChecked] = useArray([false, false]);
  const [closureReason, setClosureReason] = useState("");
  const complete = checked.every((check) => check) && closureReason !== "";
  const error = useErrorConsumer();
  const [requestStatus, setRequestStatus] = useState("initial");

  return (
    <div className={styles["finalize-closure-body"]}>
      <div className={styles["oh-no"]}>
        Oh no! We are sorry to see you go...
      </div>

      <div className={styles["let-us-know-text"]}>
        Let us know why you want to close your loan early
      </div>

      <TextInput
        variant="outlined"
        className={styles["freeform-reason"]}
        label="Tell us more..."
        onChange={(e) => setClosureReason(e.currentTarget.value)}
      />

      <Line className="my-2" />
      <div className={styles["warning-title"]}>
        This will close your Credit Builder Loan.
      </div>

      <LabeledCheckbox
        checked={checked[0]}
        label="I understand"
        onChange={(e) => {
          setChecked.at(0, e.currentTarget.checked);
        }}
      />

      <Line className="my-1" />

      <div className={styles["warning-title"]}>
        How might this affect your credit score?
      </div>

      <div className={styles["warning-body"]}>
        Depending on where you are at with your credit journey, sometimes
        closing an account can have a negative impact on your overall credit
        score.
      </div>
      <LabeledCheckbox
        className={styles["check-box"]}
        checked={checked[1]}
        label="I understand"
        onChange={(e) => {
          setChecked.at(1, e.currentTarget.checked);
        }}
      />

      <StyledButton
        Element={StatusButton}
        className={styles["close-my-loan"]}
        disabled={
          !complete || requestStatus === "pending" || closureReason === ""
        }
        styleType={
          complete && closureReason !== "" && requestStatus !== "pending"
            ? "active"
            : "pending"
        }
        onClick={() => {
          setRequestStatus("pending");
          dispatch(
            closeLoan({
              loanToken: loan.token,
              closureReason,
            })
          )
            .then(async () => {
              track("CBL - Close - Account Closed");
              setRequestStatus("success");
              await promiseDelay(1500);
              onContinue();
            })
            .catch((err) => {
              track("CBL - Close - Account Closed Failed");
              setRequestStatus("initial");
              error.throw(err.message);
            });
        }}
        shape="round"
        // @ts-expect-error
        text={{
          initial: "Close my loan",
          pending: "Closing loan...",
          success: "Loan successfully closed",
        }}
        status={requestStatus}
      />
    </div>
  );
}

function CloseConfirmedView({ onContinue }) {
  return (
    <div className={styles["close-confirmation-body"]}>
      <div className={styles["close-confirmation-title"]}>
        You have closed your Credit Builder Loan
      </div>

      <div className={styles["close-confirmation-details"]}>
        You will be able to withdraw any funds you have already contributed once
        payments have finished processing. You will still be able to access the
        Kikoff dashboard to see any future offers and keep up with your other
        accounts (if applicable).
      </div>

      <StyledButton
        className={styles["back-to-dashboard"]}
        onClick={() => onContinue()}
        shape="round"
      >
        Back to dashboard
      </StyledButton>
    </div>
  );
}

function goToDashboard() {
  if (!nativeDispatch("close"))
    Router.push("/dashboard/credit-builder-savings");
}
